import React from "react";

// Texts of the home page. The English version is written for US search
// (target keyword: "free local seo tools"), not translated word for word.
const content = {
  fr: {
    seo: {
      title: "Outils de visibilité locale gratuits | Localranker",
      description:
        "Vous voulez améliorer votre visibilité locale ? Aidez-vous de nos outils gratuits de référencement local !",
    },
    hero: {
      badge: "Répertoire d'outils de visibilité locale",
      title: "Nos outils gratuits de visibilité locale",
      subtitle:
        "Des outils gratuits et puissants pour analyser, auditer et optimiser votre présence locale sur Google. Réseaux multi-établissements et agences, demandez une démo.",
      cta: "Demander une démo",
    },
    cardLink: "Tester l'outil →",
    newTag: "Nouveau",
    tools: {
      gbpAudit: {
        title: "Audit fiche Google Business Profile",
        description:
          "Réalisez un audit de votre fiche GBP en 1 clic et commencez à optimiser votre fiche pour dépasser vos concurrents locaux.",
      },
      storeLocatorAudit: {
        title: "Audit store locator",
        description:
          "Réalisez un audit de votre store locator en toute autonomie. Collez l'URL d'une page locale et obtenez une analyse complète.",
      },
      localSerpChecker: {
        title: "Résultats Google localisés",
        description:
          "Découvrez les résultats de recherche locales sur la requête de votre choix et comparez votre référencement local face à la concurrence.",
      },
      reviewSentimentAnalysis: {
        title: "Analyse sémantique des avis clients",
        description:
          "Analysez le sentiment et les thématiques clés de vos avis clients pour identifier vos forces et axes d'amélioration.",
      },
      reviewCalculator: {
        title: "Calculateur d'avis manquants",
        description:
          "Indiquez votre nombre d'avis et votre note actuelle, et découvrez combien d'avis 5 étoiles il vous faut pour atteindre votre objectif.",
      },
      schemaGenerator: {
        title: "Générateur de données structurées locales",
        description:
          "Générez facilement des données structurées JSON-LD pour votre entreprise locale et améliorez votre référencement dans les résultats Google.",
      },
      gbpCategories: {
        title: "Recherche de catégories Google Business Profile",
        description:
          "Trouvez et copiez les catégories GBP parfaites pour votre fiche Google. Recherchez parmi des milliers de catégories en 5 langues.",
      },
      advancedSearch: {
        title: "Extraction Recherche avancée Google",
        description:
          "Construisez facilement des requêtes de recherche Google avancées avec des opérateurs comme inurl:, site:, et des termes combinés.",
      },
    },
    mission: {
      badge: "Notre mission",
      title: "Pourquoi ces outils sont gratuits ?",
      blocks: [
        {
          title: "La visibilité locale ne devrait pas être un privilège",
          text: "Trop d'entreprises passent à côté de clients simplement parce qu'elles n'ont pas accès aux bons outils d'analyse. Que vous soyez un commerce de quartier, un artisan ou un réseau de franchises, vous méritez de comprendre comment vous apparaissez dans les résultats de recherche locaux, sans avoir à investir dans des solutions coûteuses pour obtenir un premier diagnostic.",
        },
        {
          title: "Un premier diagnostic, sans engagement",
          text: "Nous construisons ces outils parce que nous pensons que la meilleure façon de prouver notre expertise, c'est de la partager. Ces analyses gratuites vous donnent un aperçu concret de votre situation et des pistes d'amélioration claires. C'est un point de départ solide pour prendre les bonnes décisions sur votre stratégie locale.",
        },
        {
          title: "Aider les entreprises à trouver leurs prochains clients",
          text: "Notre conviction est simple : chaque entreprise locale devrait pouvoir être trouvée en ligne par les clients qui la cherchent. Ces outils gratuits sont un premier pas concret. Ils vous permettent d'identifier ce qui fonctionne, ce qui manque, et par où commencer pour améliorer votre présence locale.",
        },
        {
          title: "Aller plus loin avec Localranker",
          text: (
            <>
              Ces outils vous donnent une photographie précise de votre
              situation. Pour passer à l'action, gérer vos fiches Google à
              grande échelle, centraliser vos avis, publier du contenu local,
              suivre votre positionnement dans le temps, notre plateforme{" "}
              <strong>Localranker</strong> prend le relais. Réseaux
              multi-établissements et agences l'utilisent au quotidien pour
              piloter leur visibilité locale.
            </>
          ),
        },
      ],
      cta: "Demander une démo de Localranker",
    },
  },

  en: {
    seo: {
      title: "Free Local SEO Tools | Localranker",
      description:
        "Free local SEO tools for Google: check local rankings from any location, calculate how many reviews you need, generate LocalBusiness schema and more.",
    },
    hero: {
      badge: "Local SEO toolbox",
      title: "Free Local SEO Tools",
      subtitle:
        "Free, no-signup tools to check, audit and improve how your business shows up on Google Search and Google Maps. Managing many locations or clients? Book a demo.",
      cta: "Book a demo",
    },
    cardLink: "Try it free →",
    newTag: "New",
    tools: {
      gbpAudit: {
        title: "Google Business Profile Audit",
        description:
          "Audit your Google Business Profile in one click and see what to fix to outrank local competitors.",
      },
      storeLocatorAudit: {
        title: "Store Locator SEO Audit",
        description:
          "Paste the URL of a location page and get a full SEO audit of your store locator.",
      },
      localSerpChecker: {
        title: "Local SERP Checker",
        description:
          "See Google Search and Google Maps results from any city or ZIP code, and compare your local rankings with competitors.",
      },
      reviewSentimentAnalysis: {
        title: "Review Sentiment Analysis",
        description:
          "Find out what customers love and complain about: sentiment and key topics from your online reviews.",
      },
      reviewCalculator: {
        title: "Google Review Calculator",
        description:
          "Enter your current rating and review count to see how many 5-star reviews you need to reach your target rating.",
      },
      schemaGenerator: {
        title: "Local Business Schema Generator",
        description:
          "Generate LocalBusiness JSON-LD structured data for your website, ready to copy, paste and test.",
      },
      gbpCategories: {
        title: "Google Business Profile Categories",
        description:
          "Search the full list of Google Business Profile categories and copy the best primary and secondary categories for your listing.",
      },
    },
    mission: {
      badge: "Our mission",
      title: "Why are these tools free?",
      blocks: [
        {
          title: "Local visibility shouldn't be a privilege",
          text: "Too many businesses lose customers simply because they don't have the right tools. Whether you run a neighborhood shop, a service business or a franchise network, you deserve to know how you show up in local search results, without paying for expensive software just to get a first diagnosis.",
        },
        {
          title: "A free first diagnosis, no strings attached",
          text: "We build these tools because the best way to prove our expertise is to share it. Each free check gives you a clear picture of where you stand and what to improve, so you can make the right calls for your local SEO strategy.",
        },
        {
          title: "Helping businesses get found by their next customers",
          text: "We believe every local business should be easy to find for the people searching for it. These free tools are a practical first step: they show you what works, what's missing and where to start.",
        },
        {
          title: "Go further with Localranker",
          text: (
            <>
              These tools give you a snapshot of where you stand. To act on it,
              manage your Google Business Profiles at scale, centralize reviews,
              publish local posts and track local rankings over time,{" "}
              <strong>Localranker</strong> takes over. Multi-location brands and
              agencies use it every day to run their local SEO.
            </>
          ),
        },
      ],
      cta: "Book a Localranker demo",
    },
  },
};

export default content;
